import { useApolloClient } from '@apollo/client';
import { Button, Grid, message } from 'antd';
import { Formik } from 'formik';
import { isNil, pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type CompanyWithPermissionsSpecsFragment } from '../../../api/fragments';
import {
    CreateEdmEmailSocialMediaDocument,
    type CreateEdmEmailSocialMediaMutation,
    type CreateEdmEmailSocialMediaMutationVariables,
} from '../../../api/mutations/createEdmEmailSocialMedia';
import {
    DeleteCompanyDocument,
    type DeleteCompanyMutation,
    type DeleteCompanyMutationVariables,
} from '../../../api/mutations/deleteCompany';
import {
    DeleteEdmEmailSocialMediaDocument,
    type DeleteEdmEmailSocialMediaMutation,
    type DeleteEdmEmailSocialMediaMutationVariables,
} from '../../../api/mutations/deleteEdmEmailSocialMedia';
import {
    UpdateCompanyDocument,
    type UpdateCompanyMutation,
    type UpdateCompanyMutationVariables,
} from '../../../api/mutations/updateCompany';
import {
    UpdateEdmEmailSocialMediaDocument,
    type UpdateEdmEmailSocialMediaMutation,
    type UpdateEdmEmailSocialMediaMutationVariables,
} from '../../../api/mutations/updateEdmEmailSocialMedia';
import { MaskDirection, MfaSettingsType } from '../../../api/types';
import { useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { useRuntimeConfig } from '../../../runtimeConfig';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import CompanyForm, { type CompanyFormValues, defaultSocialMediaValue, useCompanyFormValidator } from './CompanyForm';
import { useDeleteCompanyModal } from './DeleteCompanyModal';
import useHandleCompanyAssets from './useHandleCompanyAssets';
import useHandleEdmEmailSocialMediaAsset from './useHandleEdmEmailSocialMediaAsset';

export type CompanyDetailsInnerPageProps = { company: CompanyWithPermissionsSpecsFragment };

const CompanyDetailsInnerPage = ({ company }: CompanyDetailsInnerPageProps) => {
    const { t } = useTranslation(['companyDetails']);
    const companyId = company.id;

    const apolloClient = useApolloClient();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: company.versioning,
        timeZone: company?.timeZone,
    });
    const { enableGenAI } = useRuntimeConfig();

    const handleCompanyAssets = useHandleCompanyAssets();
    const handleEdmEmailSocialMediaAsset = useHandleEdmEmailSocialMediaAsset();

    const { refetch } = useCompanyContext();

    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const onSubmit = useHandleError<CompanyFormValues>(
        async values => {
            const {
                favicon,
                logo,
                font,
                mfaSettings,
                fontBold,
                edmEmailFooter,
                dataMask,
                phone,
                enableContentRefinement,
                ...settings
            } = values;

            const {
                socialMedia: socialMediaInput,
                phoneIcon,
                arrowIcon,
                emailIcon,
                ...edmEmailFooterSettings
            } = edmEmailFooter;

            const socialMedia = socialMediaInput.filter(media => !!media.url);
            const storedSocialMedia = company.edmEmailFooter.socialMedia;
            const addMedialist = socialMedia.filter(media => !media?.id);
            const vatRateSettings = settings.vatRateSettings?.vatRateTable?.length ? settings.vatRateSettings : null;

            // submitting message
            message.loading({
                content: t('companyDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateCompanyMutation, UpdateCompanyMutationVariables>({
                    mutation: UpdateCompanyDocument,
                    variables: {
                        id: companyId,
                        settings: {
                            ...settings,
                            phone: isNil(phone?.prefix) || isNil(phone?.value) ? null : phone,
                            ...(mfaSettings.enabled && { mfaSettings: { type: mfaSettings.type } }),
                            edmEmailFooter: edmEmailFooterSettings,
                            ...(dataMask.direction === MaskDirection.None
                                ? {
                                      dataMask: { count: 0, direction: dataMask.direction },
                                  }
                                : {
                                      dataMask: { count: dataMask.count, direction: dataMask.direction },
                                  }),
                            enableContentRefinement: enableGenAI ? enableContentRefinement : false,
                            vatRateSettings,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // handle company asset upload/delete
            await handleCompanyAssets(companyId, values, company);

            // loop stored social media
            const existingPromises = (storedSocialMedia || []).map(async storedMedia => {
                const currentMedia = socialMedia.find(media => media.id === storedMedia.id);

                // delete social media
                if (!currentMedia) {
                    // delete dealer social media asset first
                    return handleEdmEmailSocialMediaAsset(storedMedia.id, storedMedia.icon, company).then(() =>
                        // delete social media core
                        apolloClient.mutate<
                            DeleteEdmEmailSocialMediaMutation,
                            DeleteEdmEmailSocialMediaMutationVariables
                        >({
                            mutation: DeleteEdmEmailSocialMediaDocument,
                            variables: { socialMediaId: storedMedia.id },
                        })
                    );
                }

                // update the social media core
                return apolloClient
                    .mutate<UpdateEdmEmailSocialMediaMutation, UpdateEdmEmailSocialMediaMutationVariables>({
                        mutation: UpdateEdmEmailSocialMediaDocument,
                        variables: {
                            socialMediaId: currentMedia.id,
                            socialMediaInput: {
                                url: currentMedia.url,
                                altText: currentMedia.altText,
                            },
                        },
                    })
                    .then(() => handleEdmEmailSocialMediaAsset(currentMedia.id, currentMedia.icon, company));
            });
            // get all newly added social media
            const createPromises = addMedialist.map(async media => {
                const currentInput = pick(['url', 'altText'], media);

                return apolloClient
                    .mutate<CreateEdmEmailSocialMediaMutation, CreateEdmEmailSocialMediaMutationVariables>({
                        mutation: CreateEdmEmailSocialMediaDocument,
                        variables: { companyId: company.id, socialMediaInput: currentInput },
                    })
                    .then(createMedia =>
                        handleEdmEmailSocialMediaAsset(createMedia.data.createEdmEmailSocialMedia.id, media.icon)
                    );
            });

            await existingPromises;
            await createPromises;

            refetch();

            // inform about success
            message.success({
                content: t('companyDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/companies');
        },
        [
            company,
            t,
            apolloClient,
            companyId,
            enableGenAI,
            handleCompanyAssets,
            refetch,
            navigate,
            handleEdmEmailSocialMediaAsset,
        ]
    );

    const deleteCompanyModal = useDeleteCompanyModal();

    const onConfirmDelete = useCallback(async () => {
        try {
            // loading message
            message.loading({
                content: t('companyDetails:messages.deleteSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // delete with the API
            await apolloClient
                .mutate<DeleteCompanyMutation, DeleteCompanyMutationVariables>({
                    mutation: DeleteCompanyDocument,
                    variables: { id: companyId },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // show success
            message.success({
                content: t('companyDetails:messages.deleteSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/companies');
        } catch (error) {
            const apolloErrors = getApolloErrors(error);
            if (apolloErrors?.$root) {
                message.error(apolloErrors?.$root);
            }
        }
    }, [apolloClient, companyId, navigate, t]);

    const initialValues = useMemo((): CompanyFormValues => {
        const { edmEmailFooter } = company;

        return {
            ...pick(
                [
                    'displayName',
                    'companyName',
                    'legalName',
                    'color',
                    'theme',
                    'copyright',
                    'countryCode',
                    'marketCode',
                    'coe',
                    'ppsr',
                    'estFee',
                    'currency',
                    'email',
                    'emailSettings',
                    'smsSettings',
                    'isActive',
                    'roundings',
                    'vatRateSettings',
                    'timeZone',
                    'phone',
                    'address',
                    'description',
                    'favicon',
                    'font',
                    'fontBold',
                    'logo',
                    'sessionTimeout',
                    'passwordConfiguration',
                    'isDataPurgeEnabled',
                    'dataPurgeAfter',
                    'isInstantApprovalStatsEnabled',
                ],
                company
            ),
            languages: company.languages.map(({ id }) => id),
            mfaSettings: {
                enabled: !!company.mfaSettings,
                type: company.mfaSettings?.type || MfaSettingsType.SmsOtp,
            },
            edmEmailFooter: {
                ...edmEmailFooter,
                socialMedia:
                    !edmEmailFooter.socialMedia || edmEmailFooter.socialMedia.length === 0
                        ? [defaultSocialMediaValue]
                        : edmEmailFooter.socialMedia,
            },
            dataMask: {
                count: company.mask.count === 0 ? 1 : company.mask.count,
                direction: company.mask.direction,
            },
            enableContentRefinement: company.enableContentRefinement,
        };
    }, [company]);
    const validate = useCompanyFormValidator();

    return (
        <ConsolePageWithHeader
            extra={
                hasPermissions(company.permissions, [permissionKind.deleteCompany]) && (
                    <Button onClick={deleteCompanyModal.open} danger>
                        {t('companyDetails:actions.delete')}
                    </Button>
                )
            }
            footer={[
                hasPermissions(company.permissions, [permissionKind.updateCompany]) && (
                    <Button key="submit" form="companyForm" htmlType="submit" type="primary">
                        {t('companyDetails:actions.update')}
                    </Button>
                ),
            ]}
            onBack={() => navigate('/admin/companies')}
            title={screens.md ? t('companyDetails:title', { name: company.displayName }) : company.displayName}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="companyForm" name="companyForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <CompanyForm offset={offset} updated={updated} />
                    </Form>
                )}
            </Formik>
            {deleteCompanyModal.render(onConfirmDelete)}
        </ConsolePageWithHeader>
    );
};

export default CompanyDetailsInnerPage;
